import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { progressAPI } from "../services/api";
import { usePageTitle } from "../hooks/usePageTitle";

const Progress = () => {
  usePageTitle("My Progress");
  const [activeTab, setActiveTab] = useState("all");
  const [allProgress, setAllProgress] = useState([]);
  const [inProgress, setInProgress] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    setLoading(true);
    try {
      const [allRes, inProgressRes, completedRes] = await Promise.all([
        progressAPI.getUserProgress(),
        progressAPI.getInProgressLists(),
        progressAPI.getCompletedLists(),
      ]);

      setAllProgress(allRes.data);
      setInProgress(inProgressRes.data);
      setCompleted(completedRes.data);
    } catch (error) {
      console.error("Failed to load progress:", error);
    } finally {
      setLoading(false);
    }
  };

  const getProgressData = () => {
    switch (activeTab) {
      case "in-progress":
        return inProgress;
      case "completed":
        return completed;
      case "timed":
        // Show lists that have a best time (ever completed in timed mode)
        return allProgress.filter((p) => p.bestTimeSeconds != null);
      default:
        return allProgress;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (seconds) => {
    if (!seconds) return "N/A";
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  // Calculate stats - count lists with best times
  const timedCompletedCount = allProgress.filter(
    (p) => p.bestTimeSeconds != null,
  ).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-white mb-8">My Progress</h1>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-dark-card border border-gray-700 rounded-lg p-6">
          <h3 className="text-gray-400 text-sm mb-2">Total Lists</h3>
          <p className="text-3xl font-bold text-white">{allProgress.length}</p>
        </div>
        <div className="bg-dark-card border border-gray-700 rounded-lg p-6">
          <h3 className="text-gray-400 text-sm mb-2">In Progress</h3>
          <p className="text-3xl font-bold text-yellow-400">
            {inProgress.length}
          </p>
        </div>
        <div className="bg-dark-card border border-gray-700 rounded-lg p-6">
          <h3 className="text-gray-400 text-sm mb-2">Completed</h3>
          <p className="text-3xl font-bold text-green-400">
            {completed.length}
          </p>
        </div>
        <div className="bg-dark-card border border-gray-700 rounded-lg p-6">
          <h3 className="text-gray-400 text-sm mb-2">Timed Records</h3>
          <p className="text-3xl font-bold text-purple-400">
            {timedCompletedCount}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-gray-700 overflow-x-auto">
        <button
          onClick={() => setActiveTab("all")}
          className={`pb-3 px-4 font-semibold transition whitespace-nowrap ${
            activeTab === "all"
              ? "text-blue-400 border-b-2 border-blue-400"
              : "text-gray-400 hover:text-white"
          }`}
        >
          All ({allProgress.length})
        </button>
        <button
          onClick={() => setActiveTab("in-progress")}
          className={`pb-3 px-4 font-semibold transition whitespace-nowrap ${
            activeTab === "in-progress"
              ? "text-yellow-400 border-b-2 border-yellow-400"
              : "text-gray-400 hover:text-white"
          }`}
        >
          In Progress ({inProgress.length})
        </button>
        <button
          onClick={() => setActiveTab("completed")}
          className={`pb-3 px-4 font-semibold transition whitespace-nowrap ${
            activeTab === "completed"
              ? "text-green-400 border-b-2 border-green-400"
              : "text-gray-400 hover:text-white"
          }`}
        >
          Completed ({completed.length})
        </button>
        <button
          onClick={() => setActiveTab("timed")}
          className={`pb-3 px-4 font-semibold transition whitespace-nowrap ${
            activeTab === "timed"
              ? "text-purple-400 border-b-2 border-purple-400"
              : "text-gray-400 hover:text-white"
          }`}
        >
          Timed Records ({timedCompletedCount})
        </button>
      </div>

      {/* Progress List */}
      {getProgressData().length === 0 ? (
        <div className="text-center text-gray-400 py-12">
          No lists found.{" "}
          <Link to="/" className="text-blue-400 hover:underline">
            Start exploring!
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {getProgressData().map((progress) => (
            <Link
              key={progress.id}
              to={`/list/${progress.gameListSlug}`}
              className="block bg-dark-card hover:bg-dark-hover border border-gray-700 rounded-lg p-6 transition"
            >
              <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
                <h3 className="text-xl font-bold text-white">
                  {progress.gameListTitle}
                </h3>
                <div className="flex gap-2 flex-wrap">
                  {progress.bestTimeSeconds && (
                    <span className="px-3 py-1 bg-purple-600/20 text-purple-400 text-sm rounded">
                      Has Timed Record
                    </span>
                  )}
                  {progress.isCompleted && (
                    <span className="px-3 py-1 bg-green-600/20 text-green-400 text-sm rounded">
                      ✓ Completed
                    </span>
                  )}
                </div>
              </div>

              {/* Personal Best Stats */}
              {(progress.bestTimeSeconds ||
                progress.bestGuessesNormal ||
                progress.bestGuessesTimed) && (
                <div className="mb-4 flex flex-wrap gap-4 text-sm">
                  {/* Best Time (Timed Mode) */}
                  {progress.bestTimeSeconds && (
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-yellow-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                        />
                      </svg>
                      <span className="text-gray-400">
                        Best Time:{" "}
                        <span className="text-yellow-400 font-semibold">
                          {formatTime(progress.bestTimeSeconds)}
                        </span>
                      </span>
                    </div>
                  )}

                  {/* Last Completion Time */}
                  {progress.completionTimeSeconds && (
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-blue-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-gray-400">
                        Last Time:{" "}
                        <span className="text-white font-semibold">
                          {formatTime(progress.completionTimeSeconds)}
                        </span>
                      </span>
                    </div>
                  )}

                  {/* Best Guesses (Normal Mode) */}
                  {progress.bestGuessesNormal && (
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-green-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-gray-400">
                        Best (Normal):{" "}
                        <span className="text-green-400 font-semibold">
                          {progress.bestGuessesNormal}{" "}
                          {progress.bestGuessesNormal === 1
                            ? "guess"
                            : "guesses"}
                        </span>
                      </span>
                    </div>
                  )}

                  {/* Last Guesses (Normal Mode) */}
                  {progress.lastAttemptGuessesNormal && (
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-green-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-gray-400">
                        Last (Normal):{" "}
                        <span className="text-white font-semibold">
                          {progress.lastAttemptGuessesNormal}{" "}
                          {progress.lastAttemptGuessesNormal === 1
                            ? "guess"
                            : "guesses"}
                        </span>
                      </span>
                    </div>
                  )}

                  {/* Best Guesses (Timed Mode) */}
                  {progress.bestGuessesTimed && (
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-purple-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-gray-400">
                        Best (Timed):{" "}
                        <span className="text-purple-400 font-semibold">
                          {progress.bestGuessesTimed}{" "}
                          {progress.bestGuessesTimed === 1
                            ? "guess"
                            : "guesses"}
                        </span>
                      </span>
                    </div>
                  )}

                  {/* Last Guesses (Timed Mode) */}
                  {progress.lastAttemptGuessesTimed && (
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-purple-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-gray-400">
                        Last (Timed):{" "}
                        <span className="text-white font-semibold">
                          {progress.lastAttemptGuessesTimed}{" "}
                          {progress.lastAttemptGuessesTimed === 1
                            ? "guess"
                            : "guesses"}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-400 mb-2">
                  <span>
                    {progress.totalGuessed} / {progress.totalItems} guessed
                  </span>
                  <span>
                    {Math.round(
                      (progress.totalGuessed / progress.totalItems) * 100,
                    )}
                    %
                  </span>
                </div>
                <div className="w-full bg-dark-bg rounded-full h-2 border border-gray-600">
                  <div
                    className={`h-full rounded-full transition-all ${
                      progress.isCompleted
                        ? progress.bestTimeSeconds
                          ? "bg-purple-600"
                          : "bg-green-600"
                        : "bg-blue-600"
                    }`}
                    style={{
                      width: `${
                        (progress.totalGuessed / progress.totalItems) * 100
                      }%`,
                    }}
                  ></div>
                </div>
              </div>

              {/* Dates */}
              <div className="flex justify-between text-sm text-gray-400 flex-wrap gap-2">
                {progress.startedAt && (
                  <span>Started: {formatDate(progress.startedAt)}</span>
                )}
                {progress.completedAt ? (
                  <span>Completed: {formatDate(progress.completedAt)}</span>
                ) : progress.lastGuessedAt ? (
                  <span>Last guess: {formatDate(progress.lastGuessedAt)}</span>
                ) : null}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Progress;
